import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, Grid, MenuItem, Select } from '@mui/material';

import { PRIMARY_RED } from 'commons/styles/colors';
import {
  getBinCommoditiesAction,
  updateBinCommodity,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import S from '../styles/EditBinDialog.styles';
import { IEditableBin, IEditBinModalProps } from '../types';
import { isCommodityRequiredForBinType } from '../utils/binCommodityValidation';

export const EditBinCommodityDialog: React.FC<
  IEditBinModalProps<IEditableBin>
> = ({ onClose, openModal, location, binData }) => {
  const [bin, setBin] = useState<Partial<IEditableBin>>(binData);
  const [newCommodity, setNewCommodity] = useState<string | null>(
    binData.commodity,
  );
  const dispatch = useDispatch<AppDispatch>();

  const { enabledBinCommodityList } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  useEffect(() => {
    dispatch(getBinCommoditiesAction({ isEnabled: true }));
  }, [dispatch]);

  useEffect(() => {
    setBin(binData);
    setNewCommodity(binData.commodity);
  }, [binData]);

  const isCommodityMissing =
    isCommodityRequiredForBinType(bin.bin_type) && !newCommodity;

  const onConfirm = async (): Promise<void> => {
    if (bin && bin.bin && !isCommodityMissing) {
      dispatch(updateBinCommodity({ bin: bin.bin, newCommodity, location }));
    }
  };

  const isConfirmDisabled = (): boolean => {
    return newCommodity === bin.commodity || isCommodityMissing;
  };

  return (
    <Dialog
      onClose={onClose}
      open={openModal}
      style={{ whiteSpace: 'pre-line' }}
    >
      <S.TitleWrapper>
        <S.Title>Edit Commodity</S.Title>
      </S.TitleWrapper>
      <Grid container style={{ marginTop: '12px', width: '500px' }}>
        <S.FieldRowWrapper container item xs={12} spacing={2}>
          <Grid item xs={5}>
            <S.Text>Bin</S.Text>
          </Grid>
          <S.InputWrapper item xs={7}>
            <S.Text>{bin.bin}</S.Text>
          </S.InputWrapper>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper container item xs={12} spacing={2}>
          <Grid item xs={5}>
            <S.Text>New Commodity</S.Text>
          </Grid>
          <S.InputWrapper>
            <Select
              data-testid="dropdownEditBinCommodity"
              value={newCommodity || ''}
              onChange={(event): void =>
                setNewCommodity(event.target.value || null)
              }
              fullWidth
              style={{ marginTop: '12px' }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {enabledBinCommodityList.map((_commodity) => (
                <MenuItem value={_commodity.name}>{_commodity.name}</MenuItem>
              ))}
            </Select>
            {isCommodityMissing && (
              <p
                data-testid="textEditBinCommodityRequired"
                style={{
                  color: PRIMARY_RED,
                  fontSize: '12px',
                  margin: '4px 0 0',
                }}
              >
                Commodity is required for this bin type
              </p>
            )}
          </S.InputWrapper>
        </S.FieldRowWrapper>
      </Grid>
      <S.FooterWrapper>
        <S.SecondaryButton onClick={onClose}>Cancel</S.SecondaryButton>
        <S.PrimaryButton
          onClick={() => {
            onConfirm();
            onClose();
          }}
          disabled={isConfirmDisabled()}
        >
          Update
        </S.PrimaryButton>
      </S.FooterWrapper>
    </Dialog>
  );
};
